import { AlertTriangle, FileText, Info } from "lucide-react";
import type { PreparedReorder } from "@shared-core/data/api-types";

/**
 * "Order again from Tharstern job X" banner for CatalogueItemPage's pre-filled state (catalogue-reorder brief §11,
 * §16; v1 completion F §4.2). Says where the spec came from (the frozen copy of Tharstern), the job's archived artwork
 * when the archive holds one, and the job-versus-estimate notes. The per-field detail is rendered next to each field
 * by `ReorderFieldChip`.
 */
export function ReorderProvenanceBanner({
  prepared,
}: {
  prepared: PreparedReorder;
}) {
  const notes = prepared.reconciliation_notes ?? [];
  const divergences = notes.filter((n) => n.severity !== "info");
  const source = `${prepared.source_kind === "job" ? "job" : "estimate"} ${prepared.source_ref}`;
  const summary =
    prepared.outcome === "migrated_item"
      ? `Order again from Tharstern ${source}: the customer's own item`
      : `Order again from Tharstern ${source}`;
  const artwork = prepared.artwork_offer;

  return (
    <div className="rounded-lg border border-border bg-muted p-3" role="status">
      <div className="flex items-start gap-2">
        <Info className="size-4 mt-0.5 shrink-0 text-muted-foreground" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">
            {summary}{" "}
            <span className="font-normal text-muted-foreground">
              · read from the copy
            </span>
          </p>
          {artwork ? (
            <p className="mt-1 flex items-center gap-1.5 text-sm">
              <FileText className="size-3.5 text-muted-foreground" />
              Artwork from {artwork.from_record} (archived): {artwork.file_name}
            </p>
          ) : null}
          {divergences.length > 0 ? (
            <div className="mt-2 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
              <p className="flex items-center gap-1.5 font-medium mb-1">
                <AlertTriangle className="size-3.5" />
                {divergences.length === 1
                  ? "1 thing changed since the source job — review before quoting."
                  : `${divergences.length} things changed since the source job — review before quoting.`}
              </p>
              <ul className="mt-1 space-y-1">
                {divergences.map((n, i) => (
                  <li key={i}>{n.message}</li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
